/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState, useMemo } from "react";
import { Button } from "../../../components/button.component";
import { Card } from "../../../components/card.component";
import { useLocation } from "react-router-dom";
import { AppRouteApi } from "../../../config/appRoute";
import { CaregiverList } from "../../../features/caregiver/caregiverList.component";
import { useAppDispatch } from "../../../core/redux";
import { ActionsCaregiverTable } from "../../../core/redux/slices/sliceCaregiverTable";
import { useAppSelector } from "../../../core/redux/ReduxStore";
import { ExportButton } from "../../../components/export.component";
import { Form } from "rsuite";
import {
  DateInput,
  SelectPickerInput,
} from "../../../components/textInput.component";
import {
  useTablePaginationSearchFilter,
  IPaginationSearchFilter,
} from "../../../hooks";
import { ApiSchemaFilteredCaregiver } from "../../../models/api";
import { ApiCaregiver } from "../../../core/apis";

type FormData = {
  fromDate?: Date | null;
  toDate?: Date | null;
  expirationDateFilter?: Date | null;
};

export const PageCaregiverView = () => {
  const { state } = useLocation<{ status: string }>();
  const [dateRange, setDateRange] = useState<{
    fromDate?: Date;
    toDate?: Date;
  }>({});
  const [formData, setFormData] = useState<FormData>({});
  const [certificationStatus, setCertificationStatus] = useState("");
  const [selectedCaregiver, setSelectedCaregiver] = useState("");
  // const status = ["All", "Expired"].map((status) => ({
  //   label: status,
  //   value: status,
  // }));
  const dispatch = useAppDispatch();
  const { pageNumber, pageSize, searchKey, nextPageToken, shouldRefetch } =
    useAppSelector((state) => state.caregiverTable);

  const { data: allCaregiverData, isValidating: isLoadingCaregiverDropdown } =
    ApiCaregiver.GetAllFromCompany.Hook();

  const caregiverSelectItems = useMemo(() => {
    return (
      allCaregiverData?.map((caregiver) => ({
        label: `${caregiver.firstName} ${caregiver.lastName}`,
        value: caregiver.firstName,
      })) || []
    );
  }, [allCaregiverData]);

  const apiParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchKey: selectedCaregiver,
      NextPageToken: nextPageToken,
      CertificationStatus: certificationStatus,
      CertificationExpireDate: formData.expirationDateFilter,
      AvailableFrom: dateRange.fromDate,
      AvailableTo: dateRange.toDate,
    };
  }, [
    pageNumber,
    pageSize,
    searchKey,
    nextPageToken,
    certificationStatus,
    formData.expirationDateFilter,
    dateRange,
    selectedCaregiver,
  ]);

  const {
    apiData: patientData,
    isFetching: isPatientDataFetching,
    refetch,
  } = useTablePaginationSearchFilter<
    ApiSchemaFilteredCaregiver,
    typeof ActionsCaregiverTable
  >(ActionsCaregiverTable, {
    customParams: apiParams,
    apiRoute: AppRouteApi.Caregiver.Root(),
  });

  useEffect(() => {
    if (state) {
      setCertificationStatus(state?.status);
    } else {
      setCertificationStatus("");
    }
  }, []);

  const handleCaregiverChange = (value: string) => {
    setSelectedCaregiver(value);
    dispatch(ActionsCaregiverTable.goToPage(1));
  };

  const handleChange = useCallback((data: any, _: any) => {
    dispatch(ActionsCaregiverTable.goToPage(1));
    setFormData((prevFormData) => ({ ...prevFormData, ...data }));
  }, []);
  const getTimeChangeHandler = (name: string) => {
    return (time: Date) => {
      setDateRange((prevFormData) => ({
        ...prevFormData,
        [name]: time,
      }));
    };
  };

  const resetFilterOptions = () => {
    setFormData({
      fromDate: null,
      toDate: null,
      expirationDateFilter: null,
    });
    setDateRange({});
    setCertificationStatus("");
    setSelectedCaregiver("");
  };

  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  useEffect(() => {
    return () => {
      dispatch(ActionsCaregiverTable.resetData());
    };
  }, []);

  return (
    <Card
      header={
        <div className="row align-items-center  justify-content-between">
          <div className="col-md-8 col-12 content-box-title">
            <div>
              <p className="text-capitalize my-1 text-green-200">
                Manage Caregiver
              </p>
            </div>
            <div className="collapse-icon"></div>
          </div>
          <div className="col-md-4 col-12 mt-1">
            <form className="search-component d-flex justify-content-end">
              <ExportButton
                params={{ pageNumber, pageSize, searchKey }}
                component="Caregiver"
              />
            </form>
          </div>
        </div>
      }
    >
      <div className="mb-2 px-2 border-bottom border-1" id="filterPanel">
        {(formData.expirationDateFilter ||
          certificationStatus ||
          dateRange.fromDate ||
          dateRange.toDate ||
          selectedCaregiver) && (
          <div className="d-flex justify-content-end mb-1">
            <Button
              name="Reset Filter Options"
              className="btn-danger btn-sm"
              onClick={resetFilterOptions}
            >
              Reset Filter Options
            </Button>
          </div>
        )}

        <Form onChange={handleChange} fluid formValue={formData}>
          <div className="row">
            <div className="col-md-6">
              <div className="row">
                <SelectPickerInput
                  name="caregiver"
                  data={caregiverSelectItems}
                  value={selectedCaregiver}
                  onChange={(value) => handleCaregiverChange(value as string)}
                  searchable
                  placeholder="Select Caregiver"
                  disabled={isLoadingCaregiverDropdown}
                >
                  Filter By Caregiver
                </SelectPickerInput>

                {/* <SelectPickerInput
                    data={status}
                    name="status"
                    searchable={false}
                    value={certificationStatus}
                    renderEmpty={
                      <p className="text-muted p-3">No Data Available</p>
                    }
                    onChange={(value) => {
                      dispatch(ActionsCaregiverTable.goToPage(1));
                      setCertificationStatus(value);
                    }}
                  >
                    Filter By Certificate Status
                  </SelectPickerInput> */}
                <DateInput name="expirationDateFilter">
                  Filter By Certificate Expiration Date
                </DateInput>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <DateInput
                  name="formDate"
                  time
                  value={dateRange.fromDate}
                  timeChange={getTimeChangeHandler("fromDate")}
                >
                  Available From
                </DateInput>

                <DateInput
                  name="toDate"
                  time
                  value={dateRange.toDate}
                  timeChange={getTimeChangeHandler("toDate")}
                >
                  Available To
                </DateInput>
              </div>
            </div>
          </div>
        </Form>
      </div>
      <CaregiverList
        data={patientData?.list}
        totalCount={patientData?.totalCount || 0}
        isValidating={isPatientDataFetching}
        dateRange={dateRange}
        expirationDateFilter={formData.expirationDateFilter}
        certificationStatus={certificationStatus}
      />
    </Card>
  );
};
